package com.example.mystybag.cropImage.views

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.material3.IconButton
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.mystybag.common.ui.components.custom.CrossPlatformIcon
import com.example.mystybag.common.ui.components.custom.CrossPlatformIcons
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.getSize
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.common.expected.SCImageView
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.composeColor
import com.example.mystybag.cropImage.expected.cropImage
import com.example.mystybag.cropImage.expected.rotateImage
import com.example.mystybag.cropImage.logic.ProfileImageCropperViewModel
import com.example.mystybag.core.views.AppTheme
import kotlin.math.abs
import kotlin.math.ceil

/** Kotlin [Float.coerceIn] throws if min > max; crop bounds can briefly invert during layout or when maxY < 0. */
private fun Float.coerceInSafe(min: Float, max: Float): Float =
    coerceIn(minOf(min, max), maxOf(min, max))

@Composable
fun ProfileImageCropperView(viewModel: ProfileImageCropperViewModel) {
    UIView(viewModel) { uiState ->
        val IMAGE_SCALE_FACTOR          = 0.5
        val AVATAR_PERCENT_FRAME_WITH   = 0.7777f
        val ZOOM_STEP                   = 0.35f
        val aspectRatio = uiState.value.aspectRatio // null = square, otherwise width/height ratio

        var bitmap              by remember { mutableStateOf(uiState.value.selectedImage) }
        var imageBitmap         by remember { mutableStateOf(uiState.value.selectedImage) }
        var frameWidthPx        by remember { mutableStateOf(0f) }
        var frameHeightPx       by remember { mutableStateOf(0f) }
        var minZoomState        by remember { mutableStateOf(AVATAR_PERCENT_FRAME_WITH) }
        val maxZoomState        by remember { mutableStateOf(8f) }
        var zoom                by remember { mutableStateOf(1f) }
        var offset              by remember { mutableStateOf(Offset.Zero) }
        var size                by remember { mutableStateOf(IntSize.Zero) }
        var sizeDiff            by remember { mutableStateOf(0f) }

        fun getFrameXPosition(resizedBitmapWidth: Int): Int = ((resizedBitmapWidth / 2) - ((frameWidthPx * IMAGE_SCALE_FACTOR) / 2).toInt() - (offset.x * IMAGE_SCALE_FACTOR).toInt())
        fun getFrameYPosition(resizedBitmapHeight: Int): Int = ((resizedBitmapHeight / 2) - ((frameHeightPx * IMAGE_SCALE_FACTOR) / 2).toInt() - (offset.y * IMAGE_SCALE_FACTOR).toInt())

        LaunchedEffect(uiState.value.rotateImage) {
            if (uiState.value.rotateImage) {
                bitmap?.let { image ->
                    rotateImage(image)?.let {
                        viewModel.onImageRotated()
                        bitmap = it
                        imageBitmap = it
                        offset = Offset.Zero
                    }
                }
            }
        }

        LaunchedEffect(uiState.value.cropImage) {
            if (uiState.value.cropImage) {
                try {
                    bitmap?.let { image ->
                        val imageAspect = image.getSize().let { it.height / it.width }
                        val frameWidth  = (frameWidthPx * IMAGE_SCALE_FACTOR).toInt()
                        val frameHeight = (frameHeightPx * IMAGE_SCALE_FACTOR).toInt()
                        val newWidth    = (size.width * zoom * IMAGE_SCALE_FACTOR).toInt()
                        val newHeight   = (newWidth * imageAspect).toInt().coerceAtLeast(frameHeight)
                        val frameXPos   = getFrameXPosition(newWidth)
                        val frameYUpper = newHeight - frameHeight
                        val frameYPos   = (getFrameYPosition(newHeight) - (ceil(sizeDiff) * IMAGE_SCALE_FACTOR).toInt())
                            .coerceIn(minOf(0, frameYUpper), maxOf(0, frameYUpper))

                        cropImage(
                            image,
                            newWidth,
                            newHeight,
                            frameXPos,
                            frameYPos,
                            frameWidth,
                            frameHeight,
                            zoom,
                            uiState.value.outputWidth,
                            uiState.value.outputHeight,
                        )?.let {
                            viewModel.onImageCropped(it)
                        }
                    }
                } catch (e: Exception) {
                    e.printStackTrace()
                }
            }
        }

        BoxWithConstraints(
            modifier         = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            val outerHeight = with (LocalDensity.current) { this@BoxWithConstraints.maxHeight.toPx() }

            BoxWithConstraints(
                modifier = Modifier
                    .verticalScroll(rememberScrollState())
                    .matchParentSize()
                    .background(SR.colors.background_black.composeColor)
                    .onGloballyPositioned { coordinates ->
                        // Square avatar crop uses the legacy frame asset (~78% width). Custom aspectRatio
                        // overlay uses fillMaxWidth() + aspectRatio — crop math must use the same width.
                        val frameWidthFraction =
                            if (aspectRatio != null) 1f else AVATAR_PERCENT_FRAME_WITH
                        frameWidthPx = coordinates.size.width * frameWidthFraction
                        frameHeightPx = if (aspectRatio != null) {
                            frameWidthPx / aspectRatio
                        } else {
                            frameWidthPx // Square
                        }

                        val frameSize = if (aspectRatio != null) {
                            minOf(frameWidthPx, frameHeightPx)
                        } else {
                            frameWidthPx
                        }

                        if (size.height > 0 && size.width > 0) {
                            if (size.height < size.width) {
                                minZoomState = if (size.height < frameSize) {
                                    (frameSize / size.height).coerceIn(AVATAR_PERCENT_FRAME_WITH, maxZoomState)
                                } else {
                                    AVATAR_PERCENT_FRAME_WITH
                                }
                            } else {
                                minZoomState = if (size.width < frameSize) {
                                    (frameSize / size.width).coerceIn(AVATAR_PERCENT_FRAME_WITH, maxZoomState)
                                } else {
                                    AVATAR_PERCENT_FRAME_WITH
                                }
                            }
                            zoom = minZoomState
                        }
                        sizeDiff = (size.height - outerHeight).div(2f).coerceAtLeast(0f)
                    }
                    .pointerInput(Unit) {
                        detectTransformGestures(
                            onGesture = { _, gesturePan, gestureZoom, _ ->
                                try {
                                    val minZ = if (minZoomState.isFinite()) minZoomState.coerceIn(AVATAR_PERCENT_FRAME_WITH, maxZoomState) else AVATAR_PERCENT_FRAME_WITH
                                    val newScale = (zoom * gestureZoom).coerceIn(minZ, maxZoomState)
                                    val newOffset = offset + gesturePan
                                    zoom = newScale
                                    sizeDiff = (size.height - outerHeight).div(2f).coerceAtLeast(0f)
                                    val maxX = ((size.width * zoom) / 2f) - (frameWidthPx / 2)
                                    val maxY = ((size.height * zoom) / 2f) - (frameHeightPx / 2)
                                    offset = Offset(
                                        newOffset.x.coerceIn(-abs(maxX), maxX),
                                        newOffset.y.coerceInSafe(-abs(maxY + sizeDiff), maxY - sizeDiff)
                                    )
                                } catch (e: Exception) {
                                    e.printStackTrace()
                                }
                            }
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                imageBitmap?.let { image ->
                    SCImageView(
                        image           = image,
                        modifier        = Modifier
                            .fillMaxWidth()
                            .onSizeChanged {
                                size = it
                            }
                            .graphicsLayer {
                                translationX = offset.x
                                translationY = offset.y
                                scaleX = zoom
                                scaleY = zoom
                            },
                        contentScale    = ContentScale.FillWidth
                    )
                }
            }

            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                if (aspectRatio != null) {
                    // Rectangle frame - use programmatic overlay with border
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .aspectRatio(aspectRatio)
                            .border(2.dp, AppTheme.current.colors.surfaceBright)
                    )
                } else {
                    // Square frame - use existing avatar frame image
                    Image(
                        painter = painterResource(SRC.images.avatar_image_frame),
                        modifier = Modifier
                            .fillMaxWidth(),
                        contentScale = ContentScale.FillWidth
                    )
                }
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = p16),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(
                    onClick = {
                        val minZ = if (minZoomState.isFinite()) minZoomState.coerceIn(AVATAR_PERCENT_FRAME_WITH, maxZoomState) else AVATAR_PERCENT_FRAME_WITH
                        zoom = (zoom - ZOOM_STEP).coerceIn(minZ, maxZoomState)
                        sizeDiff = (size.height - outerHeight).div(2f).coerceAtLeast(0f)
                        val maxX = ((size.width * zoom) / 2f) - (frameWidthPx / 2)
                        val maxY = ((size.height * zoom) / 2f) - (frameHeightPx / 2)
                        offset = Offset(
                            offset.x.coerceIn(-abs(maxX), maxX),
                            offset.y.coerceInSafe(-abs(maxY + sizeDiff), maxY - sizeDiff)
                        )
                    }
                ) {
                    CrossPlatformIcon(
                        icon = CrossPlatformIcons.Remove,
                        contentDescription = "Zoom out",
                        modifier = Modifier.size(32.dp),
                        tint = AppTheme.current.colors.primary,
                        fontSize = 28.sp
                    )
                }
                IconButton(
                    onClick = {
                        val minZ = if (minZoomState.isFinite()) minZoomState.coerceIn(AVATAR_PERCENT_FRAME_WITH, maxZoomState) else AVATAR_PERCENT_FRAME_WITH
                        zoom = (zoom + ZOOM_STEP).coerceIn(minZ, maxZoomState)
                        sizeDiff = (size.height - outerHeight).div(2f).coerceAtLeast(0f)
                        val maxX = ((size.width * zoom) / 2f) - (frameWidthPx / 2)
                        val maxY = ((size.height * zoom) / 2f) - (frameHeightPx / 2)
                        offset = Offset(
                            offset.x.coerceIn(-abs(maxX), maxX),
                            offset.y.coerceInSafe(-abs(maxY + sizeDiff), maxY - sizeDiff)
                        )
                    }
                ) {
                    CrossPlatformIcon(
                        icon = CrossPlatformIcons.Add,
                        contentDescription = "Zoom in",
                        modifier = Modifier.size(32.dp),
                        tint = AppTheme.current.colors.primary,
                        fontSize = 28.sp
                    )
                }
            }
        }
    }
}